<template>
  <template v-if="isDataInit && !isLoadingInitialData">
    <router-view />
  </template>
  <NewVersionDialog
    :show="newVersionDialogVisible"
    :version="{ frontend: newVersionFrontend, backend: newVersionBackend }"
    @confirm="handleNewVersionConfirm"
  />
</template>

<script>
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { useProjectsDatasetsStore } from '@/stores/useProjectsDatasetsStore';
import { storeToRefs } from 'pinia';
import NewVersionDialog from '@/components/NewVersionDialog.vue';
import { listenForLogin } from '@/assets/js/auth/login-broadcast';
import { safeSessionStorage } from '@/utils/safeStorage';

export default {
  components: {
    NewVersionDialog,
  },
  setup() {
    const projectsDatasetsStore = useProjectsDatasetsStore();
    const { loadInitialData } = projectsDatasetsStore;
    const { isLoadingInitialData } = storeToRefs(projectsDatasetsStore);
    return { isLoadingInitialData, loadInitialData };
  },
  data() {
    return {
      routerIsReady: false,
      isDataInit: false,
    };
  },
  computed: {
    currentProject() {
      return this.$store.state.projects.currentProject;
    },
    newVersionDialogVisible() {
      return this.$store.state.appVersion.newVersionDialogVisible;
    },
    newVersionFrontend() {
      return this.$store.state.appVersion.newVersionFrontend;
    },
    newVersionBackend() {
      return this.$store.state.appVersion.newVersionBackend;
    },
    currentRoutePath() {
      if (this.routerIsReady) {
        return this.$route.path;
      }
      return null;
    },
    isTokenExpired() {
      const currentToken = this.$store.state.user.token;
      if (this.$store.state.user.token) {
        const parseJwt = (token) => {
          try {
            return JSON.parse(atob(token.split('.')[1]));
          } catch (e) {
            return null;
          }
        };
        const parsedToken = parseJwt(currentToken);
        const tokenExpireInSeconds = parsedToken.exp - (new Date().getTime() / 1000);
        if (tokenExpireInSeconds > 0) {
          return false;
        }
      }
      return true;
    },
  },
  watch: {
    $route(to, from) {
      if (from.name === 'Datasets') {
        this.$store.commit('datasets/clearDatasetsState');
      }
    },
    isTokenExpired: {
      immediate: true,
      handler(isExpired) {
        if (isExpired && this.currentRoutePath && this.currentRoutePath !== '/') {
          this.$router.push({ path: '/logout' });
        }
      },
    },
  },
  created() {
    this.stopLoginBroadcast = listenForLogin(this.handleLoginInAnotherTab);
    this.$store.commit('setTheme', "theme-auzone-light");
    // const protocol = window.location.protocol;
    // const host = window.location.host.split(':')[0];
    // Empty BACKEND_URL (gateway/serve-test) => origin-relative API base, so the
    // browser stays same-origin with the dev-server proxy whether it loaded from
    // localhost or a LAN IP (phone). Otherwise use the configured host:port.
    const host = process.env.VUE_APP_BACKEND_URL || '';
    const enterpriseServerUrl = host ? `${host}:${process.env.VUE_APP_BACKEND_PORT}` : '';
    this.$store.commit('setEnterpriseServerUrl', enterpriseServerUrl);

    if (process.env.NODE_ENV === 'development') {
      this.$store.commit('appVersion/setAllowDebug', 'yes');
    }
  },
  async beforeMount() {
    await this.loadInitialData();
    this.isDataInit = true;
  },
  async mounted() {
    this.getAppVersion();

    this.$router.isReady().then(() => {
      this.routerIsReady = true;

      // Continue refreshing tokens if still valid
      this.$nextTick(() => {
        if (!this.isTokenExpired && this.$route.path !== '/') {
          this.$store.dispatch('user/autoRefreshToken', this.$store.state.user.token);
        }
      });
    });
    window.addEventListener("beforeunload", this.handleBeforeUnload);
  },
  beforeUnmount() {
    this.stopLoginBroadcast?.();
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
  },
  methods: {
    handleLoginInAnotherTab() {
      this.stopLoginBroadcast?.();
      window.removeEventListener('beforeunload', this.handleBeforeUnload);
      this.$store.commit('user/logout');
      safeSessionStorage.removeItem('vuex');
      window.location.replace('/login');
    },
    handleBeforeUnload(event) {
      event.stopImmediatePropagation();
      event.returnValue = '';
    },
    handleNewVersionConfirm() {
      const separator = window.location.search ? '&' : '?';
      const reloadUrl = `${window.location.pathname}${window.location.search}${separator}_v=${Date.now()}${window.location.hash}`;
      window.location.href = reloadUrl;
    },
    async getAppVersion() {
      // Version mismatch during the session is also detected from fe-version/be-version
      // response headers in datastore-interface.js, which shows the same dialog.
      const dataConnect = new DatastoreConnect();
      await dataConnect.getAppVersion()
        .then((data) => {
          if (data.result) {
            const currentFrontendVersion = this.$store.state.appVersion.frontendVersion;
            const incomingFrontendVersion = data.result.frontendVersion || '';
            const versionChanged = Boolean(currentFrontendVersion && incomingFrontendVersion && currentFrontendVersion !== incomingFrontendVersion);

            // Only overwrite the store when the body actually provides a value, so we
            // never clobber the version already set from response headers.
            if (data.result.version) {
              this.$store.commit('appVersion/setVersion', data.result.version);
            }
            this.$store.commit('appVersion/setIsSaas', data.result.is_saas);
            if (incomingFrontendVersion) {
              this.$store.commit('appVersion/setFrontendVersion', incomingFrontendVersion);
            }

            if (versionChanged) {
              this.$store.commit('appVersion/showNewVersionDialog', {
                frontend: incomingFrontendVersion,
                backend: data.result.version,
              });
            }
          }
        })
        .catch((e) => console.log(e));
    },
  },
};
</script>

<style lang="scss">
@import '@/assets/css/styles.scss';
@import '@/assets/css/bootstrap-grid.min.css';

#app {
  height: 100vh;    /* fallback */
  height: 100dvh;   /* iOS Safari 15.4+: excludes dynamic toolbar from height calculation */
  width: 100vw;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  /* NOTE: safe-area insets are applied to the specific edge chrome (mobile
     Header bar, landing nav, mobile drawer) rather than here. Padding #app
     (border-box, height:100dvh) would shrink its content box below 100dvh and
     clip the dvh-sized page roots inside it. See plan Tier 1. */
}

.button-main {
  width: 120px;
  background: #095b7c;
  color: aliceblue;
  border: none;
  padding: 10px;
}

@media (hover: hover) {
  .button-main:hover {
    background: #068697;
  }
}

.side-panel {
  width: 330px;
  height: 100%;
  background-color: var(--side-panel-color);
}

#nav {
  padding: 30px;
}

#nav a {
  font-weight: bold;
  color: #2c3e50;
}

#nav a.router-link-exact-active {
  color: #42b983;
}
</style>
